<script lang="ts">
    import Vue from "vue";
    import Component from 'vue-class-component';
    import { boolCompare, isNullOrWhitespace, numCompare } from "@common/Common";
    import { ScoringVoteEntry } from "@Evaluation/AlternativesSurveys/Domain/readmodels";
    import { DiscreteScaleLevelReadModel } from "@Scales/Domain/readmodels";


    @Component({
        props: {
            propIsMeetingManager: { type: Boolean, required: true },
            propContextName: { type: String, required: true },
            propAlternativeName: { type: String, required: true },
            propVotesEntries: { type: Array, required: true },
            propScaleLevels: { type: Array, required: true },
            propValue: { type: Number, required: false },
            propComment: { type: String, required: false }
        }
    })
    export default class MeetingQuestionReviewDr extends Vue
    {
        get isMeetingManager(): boolean { return this.$props.propIsMeetingManager; }
        get contextName(): string { return this.$props.propContextName; } 
        get alternativeName(): string { return this.$props.propAlternativeName; } 
        get votesEntries(): ScoringVoteEntry[] { return this.$props.propVotesEntries as ScoringVoteEntry[]; }
        get scaleLevels(): DiscreteScaleLevelReadModel[] { return this.$props.propScaleLevels as DiscreteScaleLevelReadModel[]; }
        get value(): number | null { return this.$props.propValue as number | null; }
        get comment(): string | null { return this.$props.propComment as string | null; }

        SavingCommentHandle: any = null;
        ActiveTab: string = "votes";
        
        get QuestionScale(): number[] { return this.scaleLevels.map(a => a.Value).sort((a, b) => numCompare(a, b)); }
        get VotesEntriesView(): VoteEntryViewModel[] { return this.votesEntries.map(e => new VoteEntryViewModel(this, e)).sort((a, b) => boolCompare(a.SurveyId != null, b.SurveyId != null)); }
        get Comments(): ScoringVoteEntry[] { return this.votesEntries.filter(a => isNullOrWhitespace(a.Comment) === false); }
        get SummaryTabActive(): boolean { return this.ActiveTab === "summary"; }
        get VotesTabActive(): boolean {  return this.ActiveTab === "votes"; }
        get CommentsTabActive(): boolean { return this.ActiveTab === "notes"; }

        get AverageCells(): TdViewModel[] { return this.getDrTableCells(this.Average); }

        get Average(): number
        {
            const votesEntries = this.votesEntries.filter(a => a.Value != null && a.VoteStrength > 0);

            if (votesEntries.length === 0)
            {
                return null;
            }

            let avg = 0.0;

            for (let voteEntry of votesEntries)
            {
                avg += voteEntry.VoteStrength * voteEntry.Value;
            }

            const sumOfWeights = votesEntries.map(a => a.VoteStrength).reduce((sum, current) => sum + current, 0);
            avg = avg / sumOfWeights;
            //return Math.Round(avg, 2);

            return avg;
        }

        get HistogramValues()
        {
            const voteValues = this.votesEntries.filter(a => a.Value != null).map(a => a.Value);
            const uniqueValues = new Set([...this.QuestionScale, ...voteValues]);
            const orderedValues = Array.from(uniqueValues).sort((a, b) => b - a);

            let maxCount = 0;

            for (const value of orderedValues)
            {
                const count = voteValues.filter(a => a === value).length;
                maxCount = Math.max(maxCount, count);
            }

            return orderedValues.map(value =>
            {
                const count = voteValues.filter(a => a === value).length;
                const percentOfMax = Math.round(count / maxCount * 100);

                return {
                    Value: value,
                    Count: count,
                    PercentOfMax: percentOfMax
                };
            });
        }


        getDrTableCells(value: number | null): TdViewModel[]
        {
            const cells = this.QuestionScale.map(val => new TdViewModel(val, false, false));
            if (value == null) return cells;

            let cell = cells.find(a => Math.round(100 * a.Value) === 100 * value);

            if (cell != null)
            {
                cell.Highlighted = true;
                return cells;
            }

            const prevCount = this.QuestionScale.filter(a => a < value).length;

            const prev = cells[prevCount - 1];
            prev.Highlighted = true;
            prev.Value = value;
            prev.Colspan = 2;
            cells.splice(prevCount, 1);

            return cells;
        }

        updateComment(comment: string)
        {
            this.$emit('update-vote-comment', comment);
        }

        updateValue(value: number)
        {
            this.$emit('update-vote-value', value);
        }

        showAlternativeDetails()
        {
            this.$emit('show-alternative-details');
        }

        showCriterionDetails()
        {
            this.$emit('show-criterion-details');
        }
    }

    class VoteEntryViewModel
    {
        parent: MeetingQuestionReviewDr = null;

        Id: string;
        VotesSourceName: string;
        VoteStrength: number;
        Value: number;
        Comment: string;

        ProjectUserId?: number;
        SurveyId?: number;

        get cells(): TdViewModel[]
        {
            return this.parent.getDrTableCells(this.Value);
        } 

        constructor(parent: MeetingQuestionReviewDr, voteEntry: ScoringVoteEntry)
        {
            this.parent = parent;

            this.Id = `${voteEntry.SurveyId}.${voteEntry.ProjectUserId}`;
            this.VotesSourceName = voteEntry.VotesSourceName;
            this.VoteStrength = voteEntry.VoteStrength;
            this.Value = voteEntry.Value;
            this.Comment = voteEntry.Comment;
            this.SurveyId = voteEntry.SurveyId;
            this.ProjectUserId = voteEntry.ProjectUserId;
        }
    }


    class TdViewModel
    {
        Colspan: number;
        Highlighted: boolean;
        Value: number;

        constructor(value: number, highlighted: boolean, colspan: boolean)
        {
            this.Colspan = colspan ? 2 : 1;
            this.Highlighted = highlighted;
            this.Value = value;
        }
    }

</script>

<template>
    <div>

        <p class="text-center lead" style="font-size: 1.5rem;">
            How would you rate: <a class="text-primary" title="Show description" @click="showAlternativeDetails">{{alternativeName}}</a><br />
            in the context of: <a class="text-primary" title="Show description" @click="showCriterionDetails">{{contextName}}</a>?
        </p>

        <template v-if="isMeetingManager === true">
            <div class="card card-collapsable mt-1">
                <div class="card-header pointer" data-toggle="collapse" href="#cardQuestion">
                    Group vote
                </div>
                <div id="cardQuestion" class="card-body collapse show pt-4">
                    <div style="min-height: 450px;">
                        <div class="list-group">
                            <a v-for="l in scaleLevels" :key="l.Value" class="list-group-item list-group-item-action text-center"
                               @click="updateValue(l.Value)" :class="{ 'bg-success': value === l.Value, 'bg-white': value !== l.Value }">
                                <strong>{{l.Value}} - {{l.Name}}</strong>
                                <p v-if="l.Definition" class="mb-0">{{l.Definition}}</p>
                            </a>
                        </div>

                        <div class="text-center">
                            <button type="button" class="btn mt-3" @click="updateValue(null)" :class="value == null ? 'btn-success' : 'btn-outline-secondary'">
                                <i class="fa-regular" aria-hidden="true" :class="value == null ? 'fa-square-check' : 'fa-square'"></i>
                                Follow the average (default)
                            </button>
                        </div>

                        <br />

                        <div style="margin-top: 15px;">
                            <textarea rows="5" :value.trim="comment" @input="updateComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <template v-if="isMeetingManager === false">
            <div style="min-height: 450px;">
                <div class="list-group">
                    <a v-for="l in scaleLevels" :key="l.Value" class="list-group-item list-group-item-action text-center"
                       @click="updateValue(l.Value)" :class="{ 'bg-success': value === l.Value, 'bg-white': value !== l.Value }">
                        <strong>{{l.Value}} - {{l.Name}}</strong>
                        <p v-if="l.Definition" class="mb-0">{{l.Definition}}</p>
                    </a>
                </div>


                <div v-if="value != null" class="text-center">
                    <button type="button" class="btn btn-link mt1" @click="updateValue(null)">
                        <i class="fa fa-times" aria-hidden="true"></i> Clear answer
                    </button>
                </div>

                <br />

                <div style="margin-top: 15px;">
                    <textarea rows="5" :value.trim="comment" @input="updateComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                </div>
            </div>
        </template>

        <div class="mt-4 DirectRateVotesBrowserView" style="min-height: 500px;" v-if="isMeetingManager === true || votesEntries?.length">
            <ul class="nav nav-tabs">
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'votes'" :class="{'active': VotesTabActive}">Votes</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'summary'" :class="{'active': SummaryTabActive}">Chart</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'notes'" :class="{'active': CommentsTabActive}">Comments</a>
                </li>
            </ul>

            <div class="tab-content">
                <div v-if="VotesTabActive" :class="{'active': VotesTabActive}" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <tbody>
                                <tr style="text-align: center;">
                                    <td style="text-align: left;">
                                        <strong>Average ({{Average != null ? Average.toFixedIfNeeded(2) : 'N/A' }})</strong>
                                    </td>
                                    <td v-for="cell in AverageCells"
                                        :class="{ 'bg-color-gray': cell.Highlighted }" :colspan="cell.Colspan" :style="{'width': cell.Colspan === 1 ? '5%' : '10%'}">
                                        <span :class="{ 'fg-color-white': cell.Highlighted }">
                                            {{cell.Value.toFixedIfNeeded(2)}}
                                        </span>
                                    </td>
                                </tr>

                                <tr style="text-align: center;" v-for="e in VotesEntriesView" :key="e.Id">
                                    <td style="text-align: left;">
                                        {{e.VotesSourceName ?? '???'}}
                                    </td>
                                    <td v-for="cell in e.cells" :key="cell.Value" :colspan="cell.Colspan" :class="{ 'bg-color-green': cell.Highlighted }">
                                        <span :class="{ 'fg-color-white': cell.Highlighted }">
                                            {{cell.Value.toFixedIfNeeded(2)}}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="SummaryTabActive" :class="{'active': SummaryTabActive} " class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table">
                            <tbody>
                                <tr style="text-align: center;" class="bottom noborder">
                                    <td v-for="v in HistogramValues" style="height: 400px; border: none; padding-top: 20px;">
                                        <div v-if="v.Count">{{v.Count}}</div>
                                        <div class="bg-color-green2" :style="{'height': v.PercentOfMax + '%'}"></div>
                                    </td>
                                </tr>
                                <tr style="text-align: center;">
                                    <td v-for="v in HistogramValues" style="width: 5%;">
                                        {{v.Value.toFixedIfNeeded(2)}}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="CommentsTabActive" :class="{ 'active': CommentsTabActive }" class="tab-pane pt-2">
                    <blockquote class="blockquote" v-for="c in Comments">
                        <p class="mb-0">{{c.Comment}}</p>
                        <footer class="blockquote-footer">{{c.VotesSourceName ?? '???'}}</footer>
                    </blockquote>
                </div>
            </div>
        </div>

    </div>
</template>

<style scoped>
    .DirectRate:hover {
        border-color: rgba(86, 86, 90, 0.25);
    }

    .DirectRateVotesBrowserView tr.bottom td {
        vertical-align: bottom !important;
    }

</style>